import React, { useEffect, useState } from 'react';
import { X, UserPlus, AlertCircle } from 'lucide-react';
import { api, ApiError } from '../../lib/api';

// AssignShooterDialog — Ops-side modal for routing an unassigned booking
// or reassigning a declined one. Two flavors per spec §4 fallback case:
//   1. Real Media-role user (preferred — full handshake)
//   2. External fallback as free-text (name + phone + optional email).
//      Bypasses the 24h handshake; Ops coordinates verbally.
//
// Uses the admin users endpoint with role=media filter to populate the
// shooter dropdown. Falls back to "external" tab when there's no
// available Media-role user.

export default function AssignShooterDialog({ event, onClose, onAssigned }) {
  const [mode, setMode] = useState('user'); // 'user' | 'external'
  const [mediaUsers, setMediaUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    api.get('/api/admin/users?role=media').then((d) => {
      if (!alive) return;
      const list = (d?.users || []).filter((u) => u.isActive);
      setMediaUsers(list);
      if (list.length > 0) setSelectedUserId(list[0].id);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const body = mode === 'user'
        ? { assignedUserId: selectedUserId }
        : { externalFallbackName: name, externalFallbackPhone: phone, externalFallbackEmail: email || null };
      const r = await api.post(`/api/admin/media/events/${event.id}/assign`, body);
      onAssigned?.(r);
      onClose?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to assign');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-base font-semibold text-white tracking-tight">Assign shooter</h2>
            <p className="text-[12px] text-gray-500 mt-0.5 truncate" title={event.eventTitle}>
              {event.eventTitle}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-2 flex items-center gap-1 border-b border-white/[0.04]">
          <TabBtn active={mode === 'user'} onClick={() => setMode('user')}>Media user</TabBtn>
          <TabBtn active={mode === 'external'} onClick={() => setMode('external')}>External fallback</TabBtn>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[12px] text-red-300 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          {mode === 'user' ? (
            <>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Shooter</span>
                {mediaUsers.length === 0 ? (
                  <div className="text-[12px] text-amber-300">No active media-role users. Add one in Users, or switch to External fallback.</div>
                ) : (
                  <select required value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} className={inputCls}>
                    {mediaUsers.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
                  </select>
                )}
              </label>
              <div className="text-[11px] text-gray-500">
                Triggers a fresh 24-hour confirmation window. The shooter gets a notification to accept or decline.
              </div>
            </>
          ) : (
            <>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Name *</span>
                <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Photographer" className={inputCls} />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Phone *</span>
                <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="212-555-9999" className={inputCls} />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Email (optional)</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@example.com" className={inputCls} />
              </label>
              <div className="text-[11px] text-amber-200/80">
                External fallback bypasses the 24h handshake. Ops coordinates verbally and the booking is marked confirmed immediately.
              </div>
            </>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-[12px] text-gray-400 hover:text-white">
              Cancel
            </button>
            <button type="submit" disabled={submitting || (mode === 'user' && mediaUsers.length === 0)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-medium hover:bg-[#9BE22E] disabled:opacity-50">
              <UserPlus className="w-3.5 h-3.5" />
              {submitting ? 'Assigning…' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 py-2 text-[12px] border-b-2 -mb-px transition-colors ${
        active ? 'border-[#B0FC38] text-white' : 'border-transparent text-gray-500 hover:text-gray-300'
      }`}>
      {children}
    </button>
  );
}

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px] placeholder-gray-600 focus:outline-none focus:border-[#B0FC38]/40';
